import { DELETED } from './special.js';
import { normalize as arrayNormalize } from './schemas/Array.js';
import { normalize as objectNormalize } from './schemas/Object.js';

const visit = (value, parent, key, schema, addEntity, visitedEntities) => {
  if (!value || !schema) {
    return value;
  }

  if (schema.normalize && typeof schema.normalize === 'function') {
    if (typeof value !== 'object') return value;
    return schema.normalize(value, parent, key, visit, addEntity, visitedEntities);
  } // serializable


  if (typeof schema === 'function') {
    return new schema(value);
  }

  if (typeof value !== 'object' || typeof schema !== 'object') return value;
  const method = Array.isArray(schema) ? arrayNormalize : objectNormalize;
  return method(schema, value, parent, key, visit, addEntity, visitedEntities);
};

const addEntities = (entities, indexes, existingEntities, existingIndexes, entityMeta, meta) => (schema, processedEntity, id) => {
  const schemaKey = schema.key;

  if (!(schemaKey in entities)) {
    entities[schemaKey] = {};
    existingEntities[schemaKey] = { ...existingEntities[schemaKey]
    };
    entityMeta[schemaKey] = { ...entityMeta[schemaKey]
    };
  }

  const existingEntity = entities[schemaKey][id];

  if (existingEntity) {
    entities[schemaKey][id] = schema.merge(existingEntity, processedEntity);
  } else {
    // TODO: eventually assume this exists and don't check for conditional. probably early 2022
    const entityExpiresAt = schema.expiresAt ? schema.expiresAt(meta, processedEntity) : meta.expiresAt;
    const inStoreEntity = existingEntities[schemaKey][id]; // this case we already have this entity in store

    if (inStoreEntity) {
      var _entityMeta$schemaKey, _entityMeta$schemaKey2, _entityMeta$schemaKey3, _meta$fetchedAt, _entityMeta$schemaKey4, _entityMeta$schemaKey5;

      const inStoreMeta = entityMeta[schemaKey][id];
      const useIncoming = // we may have in store but not in meta; so this existance check is still important
      !inStoreMeta || ( // useIncoming should not be used with legacy optimistic
      schema.useIncoming && meta.fetchedAt ? schema.useIncoming(inStoreMeta, meta, inStoreEntity, processedEntity) : entityMeta[schemaKey][id].date <= meta.date);

      if (useIncoming) {
        if (typeof processedEntity !== typeof inStoreEntity) {
          entities[schemaKey][id] = processedEntity;
        } else {
          entities[schemaKey][id] = schema.merge(inStoreEntity, processedEntity);
        }
      } else {
        entities[schemaKey][id] = inStoreEntity;
      }

      entityMeta[schemaKey][id] = {
        expiresAt: Math.max(entityExpiresAt, (_entityMeta$schemaKey = entityMeta[schemaKey][id]) == null ? void 0 : _entityMeta$schemaKey.expiresAt),
        date: Math.max(meta.date, (_entityMeta$schemaKey2 = (_entityMeta$schemaKey3 = entityMeta[schemaKey][id]) == null ? void 0 : _entityMeta$schemaKey3.date) != null ? _entityMeta$schemaKey2 : 0),
        fetchedAt: Math.max((_meta$fetchedAt = meta.fetchedAt) != null ? _meta$fetchedAt : 0, (_entityMeta$schemaKey4 = (_entityMeta$schemaKey5 = entityMeta[schemaKey][id]) == null ? void 0 : _entityMeta$schemaKey5.fetchedAt) != null ? _entityMeta$schemaKey4 : 0)
      };
    } else {
      var _meta$fetchedAt2;

      entities[schemaKey][id] = processedEntity;
      entityMeta[schemaKey][id] = {
        expiresAt: entityExpiresAt,
        date: meta.date,
        fetchedAt: (_meta$fetchedAt2 = meta.fetchedAt) != null ? _meta$fetchedAt2 : meta.date
      };
    }
  } // update index


  if (Array.isArray(schema.indexes)) {
    const entity = entities[schemaKey][id];

    if (!(schemaKey in indexes)) {
      indexes[schemaKey] = {};
      existingIndexes[schemaKey] = { ...existingIndexes[schemaKey]
      };
    }

    for (const index of schema.indexes) {
      if (!(index in indexes[schemaKey])) {
        existingIndexes[schemaKey][index] = indexes[schemaKey][index] = {};
      }

      const indexMap = indexes[schemaKey][index];

      if (existingEntity) {
        delete indexMap[existingEntity[index]];
      } // entity already in cache but the index changed


      if (existingEntities[schemaKey] && existingEntities[schemaKey][id] && existingEntities[schemaKey][id][index] !== entity[index]) {
        indexMap[existingEntities[schemaKey][id][index]] = DELETED;
      }

      if (index in entity) {
        indexMap[entity[index]] = id;
      }
      /* istanbul ignore next */
      else if ( // eslint-disable-next-line no-undef
      process.env.NODE_ENV !== 'production') {
        console.warn(`Index not found in entity. Indexes must be top-level members of your entity.
Index: ${index}
Entity: ${JSON.stringify(entity, undefined, 2)}`);
      }
    }
  } // set this after index updates so we know what indexes to remove from


  existingEntities[schemaKey][id] = entities[schemaKey][id];
};

function expectedSchemaType(schema) {
  return ['object', 'function'].includes(typeof schema) ? 'object' : typeof schema;
} // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types


export const normalize = function (input, schema) {
  let existingEntities = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  let existingIndexes = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
  let existingEntityMeta = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : {};
  let meta = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : {
    date: Date.now(),
    expiresAt: Infinity,
    fetchedAt: 0
  };
  // no schema means we don't process at all
  if (schema === undefined) return {
    entities: existingEntities,
    indexes: existingIndexes,
    result: input,
    entityMeta: existingEntityMeta
  };
  const schemaType = expectedSchemaType(schema);

  if (input === null || typeof input !== schemaType && !(schema.key !== undefined && typeof input === 'string')) {
    /* istanbul ignore else */
    if (process.env.NODE_ENV !== 'production') {
      const parseWorks = input => {
        try {
          return typeof JSON.parse(input) !== 'string';
        } catch (e) {
          return false;
        }
      };

      if (typeof input === 'string' && parseWorks(input)) {
        throw new Error(`Normalizing a string, but this does match schema.

Parsing this input string as JSON worked. This likely indicates fetch function did not parse
the JSON. By default, this only happens if "content-type" header includes "json".
See https://resthooks.io/docs/guides/custom-networking for more information

  Schema: ${JSON.stringify(schema, undefined, 2)}
  Input: "${input}"`);
      } else {
        throw new Error(`Unexpected input given to normalize. Expected type to be "${schemaType}", found "${input === null ? 'null' : typeof input}".

          Schema: ${JSON.stringify(schema, undefined, 2)}
          Input: "${input}"`);
      }
    } else {
      throw new Error(`Unexpected input given to normalize. Expected type to be "${schemaType}", found "${input === null ? 'null' : typeof input}".`);
    }
  }

  const newEntities = {};
  const newIndexes = {};
  const entities = { ...existingEntities
  };
  const indexes = { ...existingIndexes
  };
  const entityMeta = { ...existingEntityMeta
  };
  const addEntity = addEntities(newEntities, newIndexes, entities, indexes, entityMeta, meta);
  const visitedEntities = {};
  const result = visit(input, input, undefined, schema, addEntity, visitedEntities);
  return {
    entities,
    indexes,
    result,
    entityMeta
  };
};